import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Notice, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSchool } from '../lib/school';
import './Page.css';

import './Frameworks.css';
import { Dialog } from '../components/Dialog';
import { Field } from '../components/ui';
import {
  addToRequest,
  createRequest,
  getMyRequests,
  isOpenRequest,
  PROGRESS_LABEL,
  uploadRequestDocument,
  withdrawRequest,
  type FrameworkRequest,
  type TypedStructure,
} from '../lib/frameworks';
import { FileDrop } from '../components/FileDrop';
import { StructureInput, emptyArea, filledStructure } from '../components/StructureInput';
import { relativeDate } from '../lib/school';

/** Said the same way in setup and here (O-06). */
export const CUSTOM_FRAMEWORK_NOTE = 'Need your own framework? Request it from Frameworks and we’ll build it for you.';

interface FrameworkRow {
  id: string;
  name: string;
  description: string | null;
  status?: string;
  school_id: string | null;
  domain_count?: number;
}
interface Outline {
  id: string;
  domains: { id: string; name: string; indicators: { id: string; name: string; description: string }[] }[];
}

/** What your school is scored against, what else it can use, and what each looks for. */
export function FrameworksPage() {
  const school = useSchool();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['frameworks'],
    queryFn: async () => (await api<{ data: FrameworkRow[] }>('/frameworks')).data.filter((f) => !f.status || f.status === 'approved'),
  });
  const [open, setOpen] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const defaultId = school.data?.default_framework_id ?? null;
  const frameworks = list.data ?? [];
  const current = frameworks.find((f) => f.id === defaultId) ?? null;
  const others = frameworks.filter((f) => f.id !== defaultId);

  const use = async (f: FrameworkRow) => {
    setSwitching(f.id);
    setMessage(null);
    try {
      await api('/schools/mine/default-framework', { method: 'PUT', body: JSON.stringify({ frameworkId: f.id }) });
      await qc.invalidateQueries({ queryKey: ['school'] });
      setMessage({ tone: 'success', text: `New lessons at ${school.data?.name ?? 'your organisation'} will be scored against ${f.name}.` });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setSwitching(null);
    }
  };

  if (list.isLoading || school.isLoading) return <Spinner />;
  if (list.isError) return <Notice tone="error">We couldn&rsquo;t load frameworks. Refresh the page to try again.</Notice>;

  return (
    <div className="page">
      <header className="page-head">
        <h1>Frameworks</h1>
        <p className="muted">Every lesson is scored against your organisation&rsquo;s framework. Switching only affects lessons recorded from now on.</p>
      </header>

      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

      <section className="panel panel-tint fw-current">
        <p className="eyebrow">{school.data?.name ?? 'Your organisation'} scores lessons against</p>
        <h2>{current?.name ?? 'The built-in Baseline framework'}</h2>
        {current?.description ? <p className="muted">{current.description}</p> : null}
        {current ? <FrameworkOutline id={current.id} open={open === current.id} onToggle={() => setOpen(open === current.id ? null : current.id)} /> : null}
      </section>

      {others.length ? (
        <section className="page">
          <h2>Other frameworks you can use</h2>
          <div className="panel">
            <div className="list">
              {others.map((f) => (
                <div key={f.id} className="fw-row">
                  <div className="fw-row-top">
                    <div className="list-main">
                      <p className="list-title">{f.name}</p>
                      {f.description ? <p className="list-sub">{f.description}</p> : null}
                    </div>
                    <Button variant="secondary" loading={switching === f.id} onClick={() => use(f)}>
                      Use this
                    </Button>
                  </div>
                  <FrameworkOutline id={f.id} open={open === f.id} onToggle={() => setOpen(open === f.id ? null : f.id)} />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <FrameworkRequests />
    </div>
  );
}

export function FrameworkOutline({ id, open, onToggle }: { id: string; open: boolean; onToggle: () => void }) {
  const outline = useQuery({ queryKey: ['framework', id], queryFn: () => api<Outline>(`/frameworks/${id}`), enabled: open });
  return (
    <div className="fw-outline">
      <button className="link-button" onClick={onToggle} aria-expanded={open}>
        {open ? 'Hide what it looks for' : 'See what it looks for'}
      </button>
      {open ? (
        outline.isLoading ? (
          <Spinner />
        ) : (
          <div className="fw-domains">
            {(outline.data?.domains ?? []).map((d) => (
              <div key={d.id} className="fw-domain">
                <h3>{d.name}</h3>
                <ul>
                  {d.indicators.map((i) => (
                    <li key={i.id}>
                      <strong>{i.name}</strong>
                      {i.description ? <span className="muted"> {i.description}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}

/**
 * Asking EducAitor for a framework: send the document, a link or the text,
 * and follow it until it's ready (then it appears above, ready to use).
 */
function FrameworkRequests() {
  const qc = useQueryClient();
  const requests = useQuery({
    queryKey: ['framework-requests'],
    queryFn: getMyRequests,
    // Follow Being built → Checking while it's with us.
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'in_progress') ? 20_000 : false),
  });
  const [adding, setAdding] = useState<FrameworkRequest | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [link, setLink] = useState('');
  const [details, setDetails] = useState('');
  // Send a document (file, link or pasted), or type the areas and indicators in.
  const [mode, setMode] = useState<'document' | 'typed'>('document');
  const [structure, setStructure] = useState<TypedStructure>({ areas: [emptyArea()] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setName('');
    setPurpose('');
    setFile(null);
    setLink('');
    setDetails('');
    setMode('document');
    setStructure({ areas: [emptyArea()] });
    setError(null);
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError('Give the framework a name.');
    const typed = filledStructure(structure);
    if (mode === 'typed') {
      if (!typed.areas.length) return setError('Add at least one area with its indicators.');
      if (typed.areas.some((a) => !a.name)) return setError('Give every area a name.');
      if (typed.areas.some((a) => !a.indicators.length)) return setError('Add at least one indicator to every area.');
    } else if (!file && !link.trim() && !details.trim()) {
      return setError('Attach the framework, add a link to it, or paste it in.');
    }
    setBusy(true);
    try {
      const doc = mode === 'document' && file ? await uploadRequestDocument(file) : null;
      await createRequest({
        name: name.trim(),
        purpose: purpose.trim() || undefined,
        details: details.trim() || undefined,
        ...(mode === 'typed'
          ? { structure: typed }
          : { link: link.trim() || undefined, fileKey: doc?.fileKey, fileName: doc?.fileName }),
      });
      await qc.invalidateQueries({ queryKey: ['framework-requests'] });
      reset();
      setOpen(false);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'That didn’t send. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const rows: FrameworkRequest[] = requests.data ?? [];
  // One request open at a time per organisation.
  const inProgress = rows.find(isOpenRequest);
  return (
    <section className="page">
      <div className="fw-request-head">
        <div>
          <h2>Your own framework</h2>
          <p className="muted">Send us the framework you use and we&rsquo;ll build it into EducAitor for you. We&rsquo;ll email you when it&rsquo;s ready.</p>
        </div>
        <Button variant="secondary" onClick={() => setOpen(true)} disabled={!!inProgress}>
          Request a framework
        </Button>
      </div>
      {inProgress && !sent ? (
        <p className="muted">One request at a time: you can send another once &ldquo;{inProgress.name}&rdquo; is ready, or withdraw it while it&rsquo;s waiting.</p>
      ) : null}
      {sent ? <Notice tone="success">Request sent. We&rsquo;ll email you when it&rsquo;s ready.</Notice> : null}
      {rows.length ? (
        <div className="panel">
          <div className="list">
            {rows.map((r) => (
              <div key={r.id} className="list-row fw-request-row">
                <div className="list-main">
                  <p className="list-title">{r.name}</p>
                  <p className="list-sub">
                    Requested {relativeDate(r.createdAt).toLowerCase()}
                    {r.requestedBy ? ` by ${r.requestedBy}` : ''}
                    {r.status === 'ready' && r.frameworkName ? ` · ready as ${r.frameworkName}` : ''}
                  </p>
                  {(r.status === 'declined' || r.status === 'needs_info') && r.adminNote ? <p className="fw-request-note">{r.adminNote}</p> : null}
                  {r.status === 'needs_info' ? (
                    <div className="fw-request-actions">
                      <Button onClick={() => setAdding(r)}>
                        Add more
                      </Button>
                      <WithdrawButton id={r.id} />
                    </div>
                  ) : null}
                </div>
                <span className={`chip${r.status === 'ready' || r.status === 'needs_info' ? ' chip-accent' : ''}`}>{PROGRESS_LABEL[r.progress]}</span>
                {r.status === 'requested' ? <WithdrawButton id={r.id} /> : <span />}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <AddMoreDialog request={adding} onClose={() => setAdding(null)} />
      <Dialog open={open} onClose={() => !busy && setOpen(false)} title="Request a framework" wide={mode === 'typed'}>
        <Field id="fwr-name" label="Framework name" placeholder="e.g. Our teaching and learning standards" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Field
          id="fwr-purpose"
          label="Who it's for (optional)"
          placeholder="e.g. All teaching staff, or our careers team"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
        />
        <div className="segmented fw-mode" role="group" aria-label="How to send it">
          <button type="button" aria-pressed={mode === 'document'} onClick={() => setMode('document')}>
            Send a document
          </button>
          <button type="button" aria-pressed={mode === 'typed'} onClick={() => setMode('typed')}>
            Type it in
          </button>
        </div>

        {mode === 'document' ? (
          <>
            <div className="fw-guide">
              <p className="fw-guide-title">What to send</p>
              <p>Whatever you already use works: an observation form, your teaching and learning standards, or a policy. It helps if it:</p>
              <ul>
                <li>has a heading for each area, with the points for it underneath</li>
                <li>says what good teaching looks or sounds like</li>
                <li>is a text document, not a scan or photo</li>
              </ul>
              <p>We turn it into areas and indicators that can be heard in a lesson recording, and show you anything we couldn’t include.</p>
            </div>
            <div className="field">
              <span className="field-label" id="fwr-file-label">
                The document
              </span>
              <FileDrop id="fwr-file" file={file} onChange={setFile} />
            </div>
            <Field id="fwr-link" label="Or a link to it" placeholder="https://" value={link} onChange={(e) => setLink(e.target.value)} />
            <div className="field">
              <label htmlFor="fwr-details">Or paste it in, and anything we should know</label>
              <textarea id="fwr-details" className="textarea" value={details} onChange={(e) => setDetails(e.target.value)} />
            </div>
          </>
        ) : (
          <>
            <p className="muted fw-typed-intro">
              Type in your areas and the indicators in each. Three to six areas with three to five indicators each works best. We’ll check each one can be heard
              in a lesson recording and fill in anything missing.
            </p>
            <StructureInput value={structure} onChange={setStructure} />
            <div className="field">
              <label htmlFor="fwr-notes">Anything we should know (optional)</label>
              <textarea id="fwr-notes" className="textarea" value={details} onChange={(e) => setDetails(e.target.value)} />
            </div>
          </>
        )}
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy}>
            Send request
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

function WithdrawButton({ id }: { id: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="ghost"
     
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await withdrawRequest(id);
        } finally {
          setBusy(false);
          await qc.invalidateQueries({ queryKey: ['framework-requests'] });
        }
      }}
    >
      Withdraw
    </Button>
  );
}

/** Answer "We need a bit more": more detail, a link or another document, and it's built again. */
function AddMoreDialog({ request, onClose }: { request: FrameworkRequest | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [details, setDetails] = useState('');
  const [link, setLink] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (busy) return;
    setDetails('');
    setLink('');
    setFile(null);
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!request) return;
    setError(null);
    if (!details.trim() && !link.trim() && !file) return setError('Add some more detail, a link or a document.');
    setBusy(true);
    try {
      const doc = file ? await uploadRequestDocument(file, request.id) : null;
      await addToRequest(request.id, {
        details: details.trim() || undefined,
        link: link.trim() || undefined,
        fileKey: doc?.fileKey,
        fileName: doc?.fileName,
      });
      await qc.invalidateQueries({ queryKey: ['framework-requests'] });
      setBusy(false);
      close();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'That didn’t send. Try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!request} onClose={close} title="Add more">
      {request?.adminNote ? <p className="fw-request-note">{request.adminNote}</p> : null}
      <div className="field">
        <label htmlFor="fwm-details">Your answer, or anything else we should know</label>
        <textarea id="fwm-details" className="textarea" value={details} onChange={(e) => setDetails(e.target.value)} autoFocus />
      </div>
      <Field id="fwm-link" label="A link (optional)" placeholder="https://" value={link} onChange={(e) => setLink(e.target.value)} />
      <div className="field">
        <span className="field-label">A document (optional)</span>
        <FileDrop
          id="fwm-file"
          file={file}
          onChange={setFile}
          hint={request?.hasFile ? 'This replaces the document you sent before' : undefined}
        />
      </div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      <div className="dialog-actions">
        <Button variant="ghost" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} loading={busy}>
          Send
        </Button>
      </div>
    </Dialog>
  );
}
